import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { isDesktop } from 'react-device-detect';

import TabNavigation from '../components/Navigation/TabNavigation';
import { LoginSelectors } from '../redux/LoginRedux';
import TopHeader from '../components/TopHeader';

// Import all pages
import HomePage from '../pages/Home/HomePage';
import NotFoundPage from '../pages/NotFound/NotFoundPage';
import DashboardPageWrapper from '../pages/Dashboard/DashboardPageWrapper';

// Auth Pages
import LoginPage from '../pages/Auth/Login';
import SignUpPage from '../pages/Auth/SignUp';
import ForgotPasswordPage from '../pages/Auth/ForgotPassword';
import TutorialPage from '../pages/Auth/Tutorial';
import TutorialPageWrapper from '../pages/Auth/Tutorial/TutorialPageWrapper';
import AuthLoadingPage from '../pages/Auth/AuthLoading';

// Goals Pages
import GoalsMainPageWrapper from '../pages/Goals/GoalsMain/GoalsMainPageWrapper';
import GoalIndividualPageWrapper from '../pages/Goals/GoalIndividual/GoalIndividualPageWrapper';
import GoalCommentPageWrapper from '../pages/Goals/GoalComment/GoalCommentPageWrapper';
import ProgressSummaryPageWrapper from '../pages/Goals/ProgressSummary/ProgressSummaryPageWrapper';
import GalleryViewerPageWrapper from '../pages/Goals/GalleryViewer/GalleryViewerPageWrapper';

// Calendar Pages
import CalendarPageWrapper from '../pages/Calendar/Calendar/CalendarPageWrapper';
import SelectBookingCategoryPageWrapper from '../pages/Calendar/SelectBookingCategory/SelectBookingCategoryPageWrapper';
import SelectDateAndTimePageWrapper from '../pages/Calendar/SelectDateAndTime/SelectDateAndTimePageWrapper';
import AppointmentSummaryPageWrapper from '../pages/Calendar/AppointmentSummary/AppointmentSummaryPageWrapper';
import MyBookingPageWrapper from '../pages/Calendar/MyBooking/MyBookingPageWrapper';
import LeaveRequestPageWrapper from '../pages/Calendar/LeaveRequest/LeaveRequestPageWrapper';
import LeaveRequestTypePageWrapper from '../pages/Calendar/LeaveRequestType/LeaveRequestTypePageWrapper';
import LeaveRequestHistoryPageWrapper from '../pages/Calendar/LeaveRequestHistory/LeaveRequestHistoryPageWrapper';
import CancelLeaveRequestPageWrapper from '../pages/Calendar/CancelLeaveRequest/CancelLeaveRequestPageWrapper';
import SubmittingMissingPageWrapper from '../pages/Calendar/SubmittingMissing/SubmittingMissingPageWrapper';
import VerifyRequestPageWrapper from '../pages/Calendar/VerifyRequest/VerifyRequestPageWrapper';
import FileUploadedPageWrapper from '../pages/Calendar/FileUploaded/FileUploadedPageWrapper';
import BookMakeUpSessionPageWrapper from '../pages/Calendar/BookMakeUpSession/BookMakeUpSessionPageWrapper';
import VerifyMakeUpSessionSchedulePageWrapper from '../pages/Calendar/VerifyMakeUpSessionSchedule/VerifyMakeUpSessionSchedulePageWrapper';
import LeaveRequestBookMakeUpSessionPageWrapper from '../pages/Calendar/LeaveRequestBookMakeUpSession/LeaveRequestBookMakeUpSessionPageWrapper';
import AddingMinutesPageWrapper from '../pages/Calendar/AddingMinutes/AddingMinutesPageWrapper';
import CancelMakeUpSessionPageWrapper from '../pages/Calendar/CancelMakeUpSession/CancelMakeUpSessionPageWrapper';
import TypeHistoryPageWrapper from '../pages/Calendar/TypeHistory/TypeHistoryPageWrapper';
import SelectLeaveRequestPageWrapper from '../pages/Calendar/SelectLeaveRequest/SelectLeaveRequestPageWrapper';

// Announcements Pages
import AnnouncementsPageWrapper from '../pages/Announcements/Announcements/AnnouncementsPageWrapper';
import AnnouncementIndividualPageWrapper from '../pages/Announcements/AnnouncementIndividual/AnnouncementIndividualPageWrapper';

// Settings Pages
import SettingsPageWrapper from '../pages/Settings/Settings/SettingsPageWrapper';
import ProfilePageWrapper from '../pages/Settings/Profile/ProfilePageWrapper';
import ChangePasswordPageWrapper from '../pages/Settings/ChangePassword/ChangePasswordPageWrapper';
import OnBoardingPageWrapper from '../pages/Settings/OnBoarding/OnBoardingPageWrapper';
import TermsAndConditionPageWrapper from '../pages/Settings/TermsAndCondition/TermsAndConditionPageWrapper';
import PrivacyPolicyPageWrapper from '../pages/Settings/PrivacyPolicy/PrivacyPolicyPageWrapper';
import DisclaimersPageWrapper from '../pages/Settings/Disclaimers/DisclaimersPageWrapper';
import AppDescriptionPageWrapper from '../pages/Settings/AppDescription/AppDescriptionPageWrapper';

// Other Pages
import WithdrawalPageWrapper from '../pages/Other/Withdrawal/WithdrawalPageWrapper';

interface AppRouterProps {
  accessToken: string | null;
}

const AppRouter: React.FC<AppRouterProps> = ({ accessToken }) => {
  const isAuthenticated = !!accessToken;
  const location = useLocation();

  // Check if current path is '/tutorial'
  const isHeaderVisible = location ? location.pathname !== '/tutorial' && location.pathname !== '/login' && location.pathname !== '/signup' : true;


  return (
    <>
      <div className="app-layout">

        {isDesktop && isHeaderVisible && (<TopHeader isAuthenticated={isAuthenticated} className="" />)}

        <main className="main-content">
          <Routes>
            {/* Root Route - Redirect based on authentication */}
            <Route path="/" element={isAuthenticated ? <Navigate to="/goals" replace /> : <Navigate to="/tutorial" replace />} />
            
            {/* Public Routes */}
            <Route path="/home" element={<HomePage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/tutorial" element={<TutorialPageWrapper />} />
            <Route path="/auth-loading" element={<AuthLoadingPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionPageWrapper />} />

            {/* Protected Routes - Dashboard */}
            <Route path="/dashboard" element={isAuthenticated ? <DashboardPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Protected Routes - Goals */}
            <Route path="/goals" element={isAuthenticated ? <GoalsMainPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/goals/individual/:goalId" element={isAuthenticated ? <GoalIndividualPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/goals/comment/:goalId" element={isAuthenticated ? <GoalCommentPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/goals/progress" element={isAuthenticated ? <ProgressSummaryPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/goals/gallery/:goalId" element={isAuthenticated ? <GalleryViewerPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/goals/individual" element={isAuthenticated ? <GoalIndividualPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Protected Routes - Calendar */}
            <Route path="/calendar" element={isAuthenticated ? <CalendarPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/select-booking-category" element={isAuthenticated ? <SelectBookingCategoryPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/select-date-and-time" element={isAuthenticated ? <SelectDateAndTimePageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/appointment-summary" element={isAuthenticated ? <AppointmentSummaryPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/my-booking" element={isAuthenticated ? <MyBookingPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/leave-request" element={isAuthenticated ? <LeaveRequestPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/leave-request-type" element={isAuthenticated ? <LeaveRequestTypePageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/leave-request-history" element={isAuthenticated ? <LeaveRequestHistoryPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/cancel-leave-request" element={isAuthenticated ? <CancelLeaveRequestPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/submitting-missing" element={isAuthenticated ? <SubmittingMissingPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/verify-request" element={isAuthenticated ? <VerifyRequestPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/file-uploaded" element={isAuthenticated ? <FileUploadedPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/book-make-up-session" element={isAuthenticated ? <BookMakeUpSessionPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/verify-make-up-session-schedule" element={isAuthenticated ? <VerifyMakeUpSessionSchedulePageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/leave-request-makeup-session" element={isAuthenticated ? <LeaveRequestBookMakeUpSessionPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/adding-minutes" element={isAuthenticated ? <AddingMinutesPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/cancel-make-up-session" element={isAuthenticated ? <CancelMakeUpSessionPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/type-history" element={isAuthenticated ? <TypeHistoryPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/calendar/select-leave-request" element={isAuthenticated ? <SelectLeaveRequestPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Protected Routes - Announcements */}
            <Route path="/announcements" element={isAuthenticated ? <AnnouncementsPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/announcements/individual/:announcementId" element={isAuthenticated ? <AnnouncementIndividualPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Protected Routes - Settings */}
            <Route path="/settings" element={isAuthenticated ? <SettingsPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/profile" element={isAuthenticated ? <ProfilePageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/change-password" element={isAuthenticated ? <ChangePasswordPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/onboarding" element={isAuthenticated ? <OnBoardingPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/terms" element={isAuthenticated ? <TermsAndConditionPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/privacy" element={isAuthenticated ? <PrivacyPolicyPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/disclaimers" element={isAuthenticated ? <DisclaimersPageWrapper /> : <Navigate to="/tutorial" />} />
            <Route path="/settings/app-description" element={isAuthenticated ? <AppDescriptionPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Protected Routes - Other */}
            <Route path="/withdrawal" element={isAuthenticated ? <WithdrawalPageWrapper /> : <Navigate to="/tutorial" />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </main>
        
        {/* Tab Navigation - only show when authenticated */}
        {!isDesktop && <TabNavigation isAuthenticated={isAuthenticated} />}
      </div>
    </>
  );
};

// Connect to Redux
const mapStateToProps = (state: any) => ({
  accessToken: LoginSelectors.getAccessToken(state),
});

export default connect(mapStateToProps)(AppRouter);
